import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const BRAINS = [
  { name: "Main Brain", role: "Decision & roadmap", state: "FROZEN", tone: "#0B4DCC" },
  { name: "Assistant Brain", role: "Independent review", state: "FROZEN", tone: "#7C3AED" },
  { name: "Executor Brain", role: "Lab execution", state: "FROZEN", tone: "#0F766E" },
];

const SECTIONS = ["Overview", "Chat", "Knowledge", "Projects", "Runs", "Approvals", "Tools", "Evidence"];
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);
const LOCAL_LAUNCHER_URL = "http://127.0.0.1:8011";
const LOCAL_LAUNCHER_HEADERS = {
  "X-Agenaix-Launcher": "brain-local-v1",
};

function localRunnerUiAllowed() {
  if (process.env.REACT_APP_BRAIN_LOCAL_RUNNER_UI !== "true") return false;
  try {
    return LOOPBACK_HOSTS.has(new URL(process.env.REACT_APP_BACKEND_URL).hostname);
  } catch {
    return false;
  }
}

export default function BrainWorkspacePage() {
  const [section, setSection] = useState("Overview");
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [chat, setChat] = useState({ state: "idle", error: null, model: null });
  const [projects, setProjects] = useState({ state: "idle", data: null, error: null });
  const [projectAction, setProjectAction] = useState({ state: "idle", target: null, error: null });
  const [tools, setTools] = useState({ state: "idle", data: null, error: null });
  const [toolAction, setToolAction] = useState({ state: "idle", target: null, error: null });
  const [projectDraft, setProjectDraft] = useState({
    title: "",
    objective: "",
    criteria: "",
    priority: "NORMAL",
  });
  const [labStatus, setLabStatus] = useState({ state: "loading", data: null });
  const [preflight, setPreflight] = useState({ state: "idle", data: null, error: null });
  const [runs, setRuns] = useState({ state: "idle", data: null, error: null });
  const [evidence, setEvidence] = useState({ state: "idle", data: null, error: null });
  const [approvals, setApprovals] = useState({ state: "idle", data: null, error: null });
  const [approvalAction, setApprovalAction] = useState({ state: "idle", target: null, error: null });
  const [approvalNote, setApprovalNote] = useState("");
  const [runsReload, setRunsReload] = useState(0);
  const [approvalsReload, setApprovalsReload] = useState(0);
  const [projectsReload, setProjectsReload] = useState(0);
  const [toolsReload, setToolsReload] = useState(0);
  const [knowledge, setKnowledge] = useState({ state: "idle", data: null, error: null });
  const [knowledgeAction, setKnowledgeAction] = useState({ state: "idle", target: null, error: null });
  const [knowledgeDraft, setKnowledgeDraft] = useState({ title: "", content: "" });
  const [knowledgeReload, setKnowledgeReload] = useState(0);
  const [launcher, setLauncher] = useState({
    state: "idle",
    action: null,
    services: null,
    error: null,
  });

  useEffect(() => {
    let active = true;
    api.get("/admin/brain/status")
      .then(({ data }) => {
        if (active) setLabStatus({ state: "ready", data });
      })
      .catch(() => {
        if (active) setLabStatus({ state: "error", data: null });
      });
    return () => { active = false; };
  }, []);

  const source = labStatus.data?.source;
  const sourceVerified = Boolean(
    labStatus.data?.adapter?.lab_connected && source?.verified
  );
  const adapter = labStatus.data?.adapter;
  const serverApiAllowed = Boolean(
    adapter?.local_api_enabled && adapter?.server_api_enabled
  );
  const brainApiUiAllowed = serverApiAllowed || localRunnerUiAllowed();
  const runnerConfigured = Boolean(
    adapter?.state === "LOCAL_RUNNER_CONFIGURED"
      && adapter?.local_api_enabled
      && adapter?.execution_enabled
      && adapter?.run_root_configured
  );
  const canRunPreflight = runnerConfigured && localRunnerUiAllowed();
  const canReadEvidence = Boolean(
    adapter?.local_api_enabled
      && adapter?.run_root_configured
      && brainApiUiAllowed
  );
  const canUseChat = Boolean(
    adapter?.local_api_enabled
      && adapter?.chat_enabled
      && adapter?.run_root_configured
      && brainApiUiAllowed
  );
  const canUseProjects = Boolean(
    adapter?.local_api_enabled
      && adapter?.projects_enabled
      && adapter?.run_root_configured
      && brainApiUiAllowed
  );
  const canUseKnowledge = Boolean(
    adapter?.local_api_enabled
      && adapter?.chat_enabled
      && adapter?.knowledge_enabled
      && adapter?.run_root_configured
      && brainApiUiAllowed
  );
  const canUseTools = Boolean(
    sourceVerified
      && adapter?.local_api_enabled
      && adapter?.projects_enabled
      && adapter?.execution_enabled
      && adapter?.builder_enabled
      && adapter?.run_root_configured
      && localRunnerUiAllowed()
  );

  useEffect(() => {
    if (section !== "Chat" || !canUseChat) return undefined;
    let active = true;
    setChat({ state: "loading", error: null, model: null });
    api.get("/admin/brain/chat")
      .then(({ data }) => {
        if (!active) return;
        setMessages(data?.messages || []);
        setChat({ state: "ready", error: null, model: null });
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code || "CHAT_UNAVAILABLE";
        if (active) setChat({ state: "error", error: code, model: null });
      });
    return () => { active = false; };
  }, [section, canUseChat]);

  useEffect(() => {
    if (section !== "Knowledge" || !canUseKnowledge) return undefined;
    let active = true;
    setKnowledge({ state: "loading", data: null, error: null });

    api.get("/admin/brain/knowledge")
      .then(({ data }) => {
        if (active) setKnowledge({ state: "ready", data, error: null });
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code || "KNOWLEDGE_UNAVAILABLE";
        if (active) setKnowledge({ state: "error", data: null, error: code });
      });

    return () => { active = false; };
  }, [section, canUseKnowledge, knowledgeReload]);

  useEffect(() => {
    const shouldLoad = (section === "Projects" && canUseProjects) || (section === "Tools" && canUseTools);
    if (!shouldLoad) return undefined;
    let active = true;
    setProjects({ state: "loading", data: null, error: null });
    api.get("/admin/brain/projects")
      .then(({ data }) => {
        if (active) setProjects({ state: "ready", data, error: null });
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code || "PROJECTS_UNAVAILABLE";
        if (active) setProjects({ state: "error", data: null, error: code });
      });
    return () => { active = false; };
  }, [section, canUseProjects, canUseTools, projectsReload]);

  useEffect(() => {
    if (section !== "Tools" || !canUseTools) return undefined;
    let active = true;
    setTools({ state: "loading", data: null, error: null });
    api.get("/admin/brain/tools")
      .then(({ data }) => {
        if (active) setTools({ state: "ready", data, error: null });
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code || "TOOLS_UNAVAILABLE";
        if (active) setTools({ state: "error", data: null, error: code });
      });
    return () => { active = false; };
  }, [section, canUseTools, toolsReload]);
  const integrationTitle = labStatus.state === "loading"
    ? "Checking source"
    : labStatus.state === "error"
      ? "Status unavailable"
      : canRunPreflight
        ? "Local runner configured"
        : sourceVerified
          ? "Frozen source verified"
          : "Source not configured";
  const integrationDetail = canRunPreflight
    ? "Local execution controls are configured. The complete frozen manifest is verified before every run."
    : sourceVerified
      ? `${source.verified_files} / ${source.required_files} source markers verified · execution control is unavailable in this client.`
    : labStatus.state === "error"
      ? "The read-only status endpoint could not be reached. All authority remains blocked."
      : "The interface remains read-only until the frozen local source is verified.";

  useEffect(() => {
    if (!["Runs", "Approvals", "Evidence"].includes(section)) return undefined;
    if (!canReadEvidence) return undefined;
    let active = true;
    setRuns({ state: "loading", data: null, error: null });
    api.get("/admin/brain/runs")
      .then(({ data }) => {
        if (active) setRuns({ state: "ready", data, error: null });
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code || "RUNS_UNAVAILABLE";
        if (active) setRuns({ state: "error", data: null, error: code });
      });
    return () => { active = false; };
  }, [section, canReadEvidence, runsReload]);

  useEffect(() => {
    if (section !== "Approvals") return undefined;
    if (!canReadEvidence) return undefined;
    let active = true;
    setApprovals({ state: "loading", data: null, error: null });
    api.get("/admin/brain/approvals")
      .then(({ data }) => {
        if (active) setApprovals({ state: "ready", data, error: null });
      })
      .catch((error) => {
        const code = error?.response?.data?.detail?.code || "APPROVALS_UNAVAILABLE";
        if (active) setApprovals({ state: "error", data: null, error: code });
      });
    return () => { active = false; };
  }, [section, canReadEvidence, approvalsReload]);

  const runPreflight = async () => {
    if (!canRunPreflight || preflight.state === "running") return;
    setPreflight({ state: "running", data: null, error: null });
    try {
      const { data } = await api.post("/admin/brain/preflight");
      setPreflight({ state: "complete", data, error: null });
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "REQUEST_FAILED";
      setPreflight({ state: "error", data: null, error: code });
    }
  };

  const openEvidence = async (runId) => {
    if (!canReadEvidence || evidence.state === "loading") return;
    setSection("Evidence");
    setEvidence({ state: "loading", data: null, error: null });
    try {
      const { data } = await api.get(`/admin/brain/evidence/${encodeURIComponent(runId)}`);
      setEvidence({ state: "ready", data, error: null });
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "EVIDENCE_UNAVAILABLE";
      setEvidence({ state: "error", data: null, error: code });
    }
  };

  const requestApproval = async (runId) => {
    if (!canReadEvidence || approvalAction.state === "running") return;
    setApprovalAction({ state: "running", target: runId, error: null });
    try {
      await api.post(`/admin/brain/approvals/${encodeURIComponent(runId)}/request`);
      setApprovalAction({ state: "idle", target: null, error: null });
      setApprovalsReload((value) => value + 1);
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "APPROVAL_REQUEST_FAILED";
      setApprovalAction({ state: "error", target: runId, error: code });
    }
  };

  const decideApproval = async (approvalId, decision) => {
    if (!canReadEvidence || approvalAction.state === "running") return;
    setApprovalAction({ state: "running", target: approvalId, error: null });
    try {
      await api.post(
        `/admin/brain/approvals/${encodeURIComponent(approvalId)}/decision`,
        { decision, note: approvalNote.trim() || null }
      );
      setApprovalNote("");
      setApprovalAction({ state: "idle", target: null, error: null });
      setApprovalsReload((value) => value + 1);
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "APPROVAL_DECISION_FAILED";
      setApprovalAction({ state: "error", target: approvalId, error: code });
    }
  };

  const buildTool = async (projectId) => {
    if (!canUseTools || toolAction.state === "running") return;
    setToolAction({ state: "running", target: projectId, error: null });
    try {
      await api.post(`/admin/brain/tools/${encodeURIComponent(projectId)}/build`);
      setToolAction({ state: "idle", target: null, error: null });
      setToolsReload((value) => value + 1);
      setRunsReload((value) => value + 1);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const code = detail?.code || "TOOL_BUILD_FAILED";
      const message = detail?.message;
      setToolAction({
        state: "error",
        target: projectId,
        error: message ? `${code}: ${message}` : code,
      });
    }
  };

  const refreshLauncherStatus = async () => {
    setLauncher((current) => ({
      ...current,
      state: "loading",
      action: null,
      error: null,
    }));

    try {
      const response = await fetch(`${LOCAL_LAUNCHER_URL}/status`, {
        headers: LOCAL_LAUNCHER_HEADERS,
      });

      const data = await response.json();

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || "LAUNCHER_STATUS_FAILED");
      }

      setLauncher({
        state: "ready",
        action: null,
        services: data.services || null,
        error: null,
      });
    } catch (error) {
      setLauncher({
        state: "error",
        action: null,
        services: null,
        error: error?.message || "LOCAL_LAUNCHER_OFFLINE",
      });
    }
  };

  const runLauncherAction = async (action) => {
    setLauncher((current) => ({
      ...current,
      state: "running",
      action,
      error: null,
    }));

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);

    try {
      const response = await fetch(`${LOCAL_LAUNCHER_URL}/${action}`, {
        method: "POST",
        headers: LOCAL_LAUNCHER_HEADERS,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok || !data?.ok) {
        throw new Error(data?.error || "LAUNCHER_ACTION_FAILED");
      }

      setLauncher({
        state: "ready",
        action: null,
        services: data.services || null,
        error: null,
      });

if (action === "stop") {
  await refreshLauncherStatus();
} else {
  const delay = action === "start" ? 12000 : 12000;

  setTimeout(() => {
    refreshLauncherStatus();
  }, delay);
}
    } catch (error) {
      clearTimeout(timeoutId);

      setLauncher((current) => ({
        ...current,
        state: "error",
        action: null,
        error:
          error?.name === "AbortError"
            ? "LAUNCHER_ACTION_TIMEOUT"
            : error?.message || "LAUNCHER_ACTION_FAILED",
      }));

      setTimeout(() => {
        refreshLauncherStatus();
      }, 1000);
    }
  };

  const addKnowledge = async (event) => {
    event.preventDefault();

    const title = knowledgeDraft.title.trim();
    const content = knowledgeDraft.content.trim();

    if (!title || !content || !canUseKnowledge || knowledgeAction.state === "running") return;

    setKnowledgeAction({ state: "running", target: "add", error: null });

    try {
      await api.post("/admin/brain/knowledge", { title, content });
      setKnowledgeDraft({ title: "", content: "" });
      setKnowledgeAction({ state: "idle", target: null, error: null });
      setKnowledgeReload((value) => value + 1);
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "KNOWLEDGE_ADD_FAILED";
      setKnowledgeAction({ state: "error", target: "add", error: code });
    }
  };

  const deleteKnowledge = async (documentId) => {
    if (!canUseKnowledge || knowledgeAction.state === "running") return;

    setKnowledgeAction({ state: "running", target: documentId, error: null });

    try {
      await api.delete(`/admin/brain/knowledge/${encodeURIComponent(documentId)}`);
      setKnowledgeAction({ state: "idle", target: null, error: null });
      setKnowledgeReload((value) => value + 1);
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "KNOWLEDGE_DELETE_FAILED";
      setKnowledgeAction({ state: "error", target: documentId, error: code });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || !canUseChat || chat.state === "sending") return;
    setChat({ state: "sending", error: null, model: chat.model });
    setDraft("");
    try {
      const { data } = await api.post("/admin/brain/chat/messages", { message: text });
      setMessages(data?.messages || []);
      setChat({ state: "ready", error: null, model: data?.model || null });
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "CHAT_SEND_FAILED";
      setDraft(text);
      setChat({ state: "error", error: code, model: chat.model });
    }
  };

  const createProject = async (event) => {
    event.preventDefault();
    const title = projectDraft.title.trim();
    const objective = projectDraft.objective.trim();
    if (!title || !objective || !canUseProjects || projectAction.state === "running") return;
    const acceptanceCriteria = projectDraft.criteria
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
    setProjectAction({ state: "running", target: "create", error: null });
    try {
      await api.post("/admin/brain/projects", {
        title,
        objective,
        acceptance_criteria: acceptanceCriteria,
        priority: projectDraft.priority,
      });
      setProjectDraft({ title: "", objective: "", criteria: "", priority: "NORMAL" });
      setProjectAction({ state: "idle", target: null, error: null });
      setProjectsReload((value) => value + 1);
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "PROJECT_CREATE_FAILED";
      setProjectAction({ state: "error", target: "create", error: code });
    }
  };

  const transitionProject = async (projectId, state) => {
    if (!canUseProjects || projectAction.state === "running") return;
    setProjectAction({ state: "running", target: projectId, error: null });
    try {
      await api.post(`/admin/brain/projects/${encodeURIComponent(projectId)}/transition`, {
        state,
        note: null,
      });
      setProjectAction({ state: "idle", target: null, error: null });
      setProjectsReload((value) => value + 1);
    } catch (error) {
      const code = error?.response?.data?.detail?.code || "PROJECT_TRANSITION_FAILED";
      setProjectAction({ state: "error", target: projectId, error: code });
    }
  };

  return (
    <AppShell subtitle="◇ ADMIN · BRAIN LAB" title="Brain Workspace">
      <div className="min-w-0 max-w-full space-y-6" data-testid="brain-workspace">
        <div className="relative overflow-hidden rounded-2xl border border-black/10 bg-gradient-to-br from-[#071B3B] via-[#0B4DCC] to-[#5B21B6] px-6 py-6 text-white shadow-sm">
          <div className="pointer-events-none absolute -right-14 -top-16 h-48 w-48 rounded-full border border-[#FFCC00]/30" />
          <div className="pointer-events-none absolute right-5 top-3 h-24 w-24 rounded-full bg-[#FFCC00]/10 blur-2xl" />

          <div className="relative flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs font-semibold tracking-[0.2em] text-[#FFE58A]">
                AGENAIX ? LOCAL INTELLIGENCE
              </div>
              <h2 className="mt-2 text-2xl font-bold tracking-tight">
                Brain Control Center
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-white/75">
                Grounded reasoning, local execution, evidence and owner-controlled approval.
              </p>
            </div>

            <span className="rounded-full border border-[#FFCC00]/40 bg-black/15 px-4 py-2 font-mono text-xs text-[#FFE58A]">
              STAGE 8 ? PASS + FROZEN
            </span>
          </div>

          <div className="relative mt-5 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5">LOCAL ONLY</span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5">NO PRODUCTION</span>
            <span className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5">OWNER APPROVAL</span>
          </div>
        </div>

        <div className="grid min-w-0 gap-3 xl:grid-cols-3">
          {BRAINS.map((brain) => (
            <div key={brain.name} className="min-w-0 rounded-xl border border-black/10 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md" data-testid={`brain-card-${brain.name.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold">{brain.name}</div>
                <span className="fx-badge" style={{ color: brain.tone, borderColor: brain.tone }}>
                  {brain.state}
                </span>
              </div>
              <div className="mt-2 text-sm text-neutral-500">{brain.role}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-1 overflow-x-auto rounded-xl border border-black/10 bg-neutral-50 p-1.5" role="tablist" aria-label="Brain workspace sections">
          {SECTIONS.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={section === item}
              onClick={() => setSection(item)}
              className={`rounded-lg px-4 py-2.5 text-sm font-medium whitespace-nowrap transition ${
                section === item
                  ? "bg-white text-[#0B4DCC] shadow-sm ring-1 ring-black/5"
                  : "text-neutral-500 hover:bg-white/70 hover:text-[#0B1F3A]"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {section === "Overview" && (
          <div className="space-y-5">
            <div className="border border-black/10 p-5 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="fx-mono-tag">LOCAL BRAIN CONTROL</div>
                  <h3 className="mt-2 text-xl font-bold">Brain launcher</h3>
                  <p className="mt-1 text-sm text-neutral-500">
                    Start, stop or restart the local Brain services on this device.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={refreshLauncherStatus}
                    disabled={launcher.state === "running" || launcher.state === "loading"}
                  >
                    {launcher.state === "loading" ? "Checking..." : "Status"}
                  </Button>

                  <Button
                    type="button"
                    onClick={() => runLauncherAction("start")}
                    disabled={
                      launcher.state === "running"
                      || (
                        launcher.services?.brain?.online
                        && launcher.services?.translator?.online
                        && launcher.services?.reranker?.online
                      )
                    }
                  >
                    {launcher.action === "start"
                      ? "Starting..."
                      : (
                        launcher.services?.brain?.online
                        && launcher.services?.translator?.online
                        && launcher.services?.reranker?.online
                      )
                        ? "Brain Online"
                        : "Start Brain"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => runLauncherAction("restart")}
                    disabled={launcher.state === "running"}
                  >
                    {launcher.action === "restart" ? "Restarting..." : "Restart"}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => runLauncherAction("stop")}
                    disabled={launcher.state === "running"}
                  >
                    {launcher.action === "stop" ? "Stopping..." : "Stop Brain"}
                  </Button>
                </div>
              </div>

              {launcher.error && (
                <div className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  Launcher unavailable: {launcher.error}
                </div>
              )}

              {launcher.services && (
                <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {Object.entries(launcher.services).map(([name, service]) => (
                    <div
                      key={name}
                      className="border border-black/10 p-3"
                    >
                      <div className="text-xs uppercase tracking-wide text-neutral-500">
                        {name}
                      </div>
                      <div className="mt-1 font-semibold">
                        {service.online ? "Online" : "Offline"}
                      </div>
                      <div className="text-xs text-neutral-500">
                        Port {service.port}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 border border-black/10 p-6">
              <div className="fx-mono-tag mb-4">TRUST BOUNDARY</div>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div><div className="text-neutral-500">Execution</div><strong>Controlled local sandbox</strong></div>
                <div><div className="text-neutral-500">Final state</div><strong>READY_FOR_OWNER_APPROVAL</strong></div>
                <div><div className="text-neutral-500">Production access</div><strong>Blocked</strong></div>
                <div><div className="text-neutral-500">Owner approval</div><strong>Required</strong></div>
              </div>
            </div>
            <div className="border border-black/10 p-6 bg-[#0B1F3A] text-white" data-testid="brain-integration-status">
              <div className="fx-mono-tag" style={{ color: "#94A3B8" }}>INTEGRATION</div>
              <div className="mt-3 text-3xl font-black tracking-tighter">{integrationTitle}</div>
              <p className="mt-3 text-sm text-[#CBD5E1]">{integrationDetail}</p>
              {canRunPreflight && (
                <div className="mt-5 border-t border-white/20 pt-4">
                  <Button
                    type="button"
                    onClick={runPreflight}
                    disabled={preflight.state === "running"}
                    className="w-full bg-white text-[#0B1F3A] hover:bg-[#E2E8F0]"
                  >
                    {preflight.state === "running" ? "Running verified preflight…" : "Run Stage 8 preflight"}
                  </Button>
                  {preflight.state === "complete" && (
                    <div className="mt-3 text-xs font-mono" data-testid="brain-preflight-result">
                      {preflight.data?.status} · {preflight.data?.source_manifest?.verified_files} files verified
                    </div>
                  )}
                  {preflight.state === "error" && (
                    <div className="mt-3 text-xs text-[#FCA5A5]" role="alert">
                      Preflight blocked: {preflight.error}
                    </div>
                  )}
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {section === "Chat" && (
          <div className="border border-black/10">
            {!canUseChat && (
              <div className="p-10 text-center" data-testid="brain-local-chat-locked">
                <div className="fx-mono-tag">LOCAL OWNER ACCESS REQUIRED</div>
                <h3 className="mt-3 text-xl font-bold">Brain Chat is unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">Chat requires the explicitly enabled direct-loopback adapter and local Ollama. No cloud provider, tools, or execution are allowed.</p>
              </div>
            )}
            {canUseChat && (
              <>
            <div className="min-h-72 p-5 space-y-3 bg-[#F8FAFC]" data-testid="brain-chat-messages">
              {chat.state === "loading" && <div className="h-60 flex items-center justify-center text-sm text-neutral-500">Loading private local conversation…</div>}
              {chat.state !== "loading" && messages.length === 0 && (
                <div className="h-60 flex items-center justify-center text-sm text-neutral-500">
                  Start a private owner conversation with the Brain Lab.
                </div>
              )}
              {messages.map((message) => {
                const isArabic = /[\u0600-\u06FF]/.test(message.content || "");

                return (
                  <div key={message.id} className="bg-white border border-black/10 p-4">
                    <div className="fx-mono-tag mb-2">
                      {message.role === "user" ? "Owner" : "Brain Lab"}
                    </div>
                    <div
                      dir="auto"
                      style={{ unicodeBidi: "plaintext" }}
                      className={`text-sm leading-7 ${
                        isArabic ? "text-right" : "text-left"
                      }`}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => (
                            <p className="mb-3 last:mb-0">{children}</p>
                          ),
                          ul: ({ children }) => (
                            <ul className={`mb-3 list-disc space-y-1 ${
                              isArabic ? "pr-6" : "pl-6"
                            }`}>
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol className={`mb-3 list-decimal space-y-1 ${
                              isArabic ? "pr-6" : "pl-6"
                            }`}>
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li>{children}</li>
                          ),
                          code: ({ children }) => (
                            <code
                              dir="ltr"
                              className="inline-block rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.9em]"
                            >
                              {children}
                            </code>
                          ),
                          strong: ({ children }) => (
                            <strong className="font-semibold text-[#0B1F3A]">
                              {children}
                            </strong>
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={submit} className="border-t border-black/10 p-4">
              <Textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Discuss a local advisory task…"
                aria-label="Brain task"
                maxLength={4000}
                className="min-h-24"
              />
              {chat.state === "error" && <div className="mt-3 text-sm text-red-700" role="alert">Brain Chat blocked: {chat.error}</div>}
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-xs text-neutral-500">Local conversation only · No tools or execution{chat.model ? ` · ${chat.model}` : ""}</span>
                <Button type="submit" disabled={!draft.trim() || chat.state === "sending"} className="fx-btn-primary">
                  {chat.state === "sending" ? "Waiting for local Brain…" : "Send to local Brain"}
                </Button>
              </div>
            </form>
              </>
            )}
          </div>
        )}

        {section === "Knowledge" && (
          <div className="border border-black/10" data-testid="brain-knowledge-section">
            <div className="p-5 border-b border-black/10 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="fx-mono-tag">LOCAL KNOWLEDGE</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Knowledge library</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  Local-only sources used by the grounded Brain retrieval pipeline.
                </p>
              </div>

              {canUseKnowledge && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setKnowledgeReload((value) => value + 1)}
                  disabled={knowledge.state === "loading"}
                >
                  Refresh
                </Button>
              )}
            </div>

            {!canUseKnowledge && (
              <div className="p-10 text-center">
                <h3 className="text-xl font-bold">Knowledge management is unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Direct loopback Brain access with local knowledge enabled is required.
                </p>
              </div>
            )}

            {canUseKnowledge && (
              <div className="p-5 space-y-6">
                <form onSubmit={addKnowledge} className="grid gap-3 border border-black/10 p-4">
                  <div className="text-sm font-semibold">Add local knowledge</div>

                  <input
                    type="text"
                    value={knowledgeDraft.title}
                    onChange={(event) => setKnowledgeDraft((current) => ({
                      ...current,
                      title: event.target.value,
                    }))}
                    placeholder="Document title"
                    className="w-full border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0B4DCC]"
                  />

                  <Textarea
                    value={knowledgeDraft.content}
                    onChange={(event) => setKnowledgeDraft((current) => ({
                      ...current,
                      content: event.target.value,
                    }))}
                    placeholder="Paste local knowledge content..."
                    rows={7}
                  />

                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-neutral-500">
                      Stored locally only. No production, deploy or Git access.
                    </div>

                    <Button
                      type="submit"
                      disabled={
                        knowledgeAction.state === "running"
                        || !knowledgeDraft.title.trim()
                        || !knowledgeDraft.content.trim()
                      }
                    >
                      Add knowledge
                    </Button>
                  </div>
                </form>

                {knowledgeAction.state === "error" && (
                  <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    Knowledge action blocked: {knowledgeAction.error}
                  </div>
                )}

                {knowledge.state === "loading" && (
                  <div className="py-8 text-center text-sm text-neutral-500">
                    Loading local knowledge...
                  </div>
                )}

                {knowledge.state === "error" && (
                  <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    Knowledge unavailable: {knowledge.error}
                  </div>
                )}

                {knowledge.state === "ready" && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <strong>{knowledge.data?.count || 0} document(s)</strong>
                      <span className="text-xs text-neutral-500">Grounded local corpus</span>
                    </div>

                    {knowledge.data?.documents?.map((document) => (
                      <div
                        key={document.document_id}
                        className="border border-black/10 p-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold">{document.title}</div>
                            <div className="mt-1 font-mono text-xs text-neutral-400 break-all">
                              {document.document_id}
                            </div>
                            <div className="mt-2 text-xs text-neutral-500">
                              {document.chunk_count || 0} chunk(s)
                              {" ? "}
                              {new Date(document.created_at).toLocaleString()}
                            </div>
                          </div>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => deleteKnowledge(document.document_id)}
                            disabled={knowledgeAction.state === "running"}
                          >
                            Delete
                          </Button>
                        </div>

                        <div className="mt-3 text-sm text-neutral-600 line-clamp-3 whitespace-pre-wrap">
                          {document.content}
                        </div>
                      </div>
                    ))}

                    {knowledge.data?.documents?.length === 0 && (
                      <div className="py-10 text-center text-sm text-neutral-500">
                        No local knowledge documents are available.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {section === "Projects" && (
          <div className="border border-black/10" data-testid="brain-projects-section">
            <div className="p-5 border-b border-black/10 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="fx-mono-tag">OWNER WORK ORDERS</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Brain projects</h2>
                <p className="mt-2 text-sm text-neutral-500">
                  Define local work for the Builder. Marking a draft ready does not execute tools or change files.
                </p>
              </div>
              {canUseProjects && (
                <Button type="button" variant="outline" onClick={() => setProjectsReload((value) => value + 1)} disabled={projects.state === "loading"}>
                  Refresh projects
                </Button>
              )}
            </div>
            {!canUseProjects && (
              <div className="p-10 text-center" data-testid="brain-local-projects-locked">
                <div className="fx-mono-tag">LOCAL OWNER ACCESS REQUIRED</div>
                <h3 className="mt-3 text-xl font-bold">Brain Projects are unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Projects require the explicitly enabled direct-loopback adapter. Production, tools and execution remain blocked.
                </p>
              </div>
            )}
            {canUseProjects && (
              <div className="grid xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
                <div className="divide-y divide-black/10 min-w-0">
                  {projects.state === "loading" && <div className="p-10 text-center text-sm text-neutral-500">Loading owner work orders…</div>}
                  {projects.state === "error" && <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">Projects blocked: {projects.error}</div>}
                  {projectAction.state === "error" && <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">Project action blocked: {projectAction.error}</div>}
                  {projects.state === "ready" && projects.data?.rejected_projects > 0 && (
                    <div className="bg-[#FFF7D6] px-5 py-3 text-sm" role="alert">{projects.data.rejected_projects} rejected project record(s) were excluded.</div>
                  )}
                  {projects.state === "ready" && projects.data?.projects?.length === 0 && <div className="p-10 text-center text-sm text-neutral-500">No local Brain projects yet.</div>}
                  {projects.state === "ready" && projects.data?.projects?.map((project) => (
                    <div key={project.project_id} className="p-5 space-y-4" data-testid={`brain-project-${project.project_id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="fx-badge" style={{ color: project.state === "READY_FOR_BUILD" ? "#0F766E" : project.state === "ARCHIVED" ? "#64748B" : "#9A6700" }}>{project.state}</span>
                            <span className="fx-badge">{project.priority}</span>
                          </div>
                          <h3 className="mt-3 text-lg font-bold">{project.title}</h3>
                          <div className="mt-1 font-mono text-xs break-all text-neutral-500">{project.project_id}</div>
                        </div>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{project.objective}</p>
                      {project.acceptance_criteria?.length > 0 && (
                        <div className="bg-[#F8FAFC] p-4">
                          <div className="fx-mono-tag mb-2">ACCEPTANCE CRITERIA</div>
                          <ul className="list-disc pl-5 space-y-1 text-sm">{project.acceptance_criteria.map((criterion) => <li key={criterion}>{criterion}</li>)}</ul>
                        </div>
                      )}
                      {project.state !== "ARCHIVED" && (
                        <div className="flex flex-wrap justify-end gap-2">
                          {project.state === "DRAFT" && <Button type="button" className="fx-btn-primary" disabled={projectAction.state === "running"} onClick={() => transitionProject(project.project_id, "READY_FOR_BUILD")}>Mark ready for Builder</Button>}
                          {project.state === "READY_FOR_BUILD" && <Button type="button" variant="outline" disabled={projectAction.state === "running"} onClick={() => transitionProject(project.project_id, "DRAFT")}>Return to draft</Button>}
                          <Button type="button" variant="outline" disabled={projectAction.state === "running"} onClick={() => transitionProject(project.project_id, "ARCHIVED")}>Archive</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <form onSubmit={createProject} className="border-t xl:border-t-0 xl:border-l border-black/10 p-5 space-y-4 bg-[#F8FAFC]">
                  <div><div className="fx-mono-tag">NEW LOCAL PROJECT</div><h3 className="mt-2 text-xl font-bold">Create work order</h3></div>
                  <label className="block text-sm font-medium">Title
                    <input value={projectDraft.title} onChange={(event) => setProjectDraft((value) => ({ ...value, title: event.target.value }))} maxLength={160} aria-label="Project title" className="mt-2 w-full border border-black/15 bg-white px-3 py-2 text-sm" placeholder="Build a local analysis tool" />
                  </label>
                  <label className="block text-sm font-medium">Objective
                    <Textarea value={projectDraft.objective} onChange={(event) => setProjectDraft((value) => ({ ...value, objective: event.target.value }))} maxLength={4000} aria-label="Project objective" className="mt-2 min-h-28 bg-white" placeholder="Describe what the Brain should study and prepare…" />
                  </label>
                  <label className="block text-sm font-medium">Acceptance criteria
                    <Textarea value={projectDraft.criteria} onChange={(event) => setProjectDraft((value) => ({ ...value, criteria: event.target.value }))} aria-label="Project acceptance criteria" className="mt-2 min-h-24 bg-white" placeholder="One criterion per line" />
                  </label>
                  <label className="block text-sm font-medium">Priority
                    <select value={projectDraft.priority} onChange={(event) => setProjectDraft((value) => ({ ...value, priority: event.target.value }))} aria-label="Project priority" className="mt-2 w-full border border-black/15 bg-white px-3 py-2 text-sm">
                      <option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option>
                    </select>
                  </label>
                  <Button type="submit" className="fx-btn-primary w-full" disabled={!projectDraft.title.trim() || !projectDraft.objective.trim() || projectAction.state === "running"}>
                    {projectAction.state === "running" && projectAction.target === "create" ? "Creating project…" : "Create draft project"}
                  </Button>
                  <div className="text-xs text-neutral-500">Planning record only · No tools · No execution · Owner review required</div>
                </form>
              </div>
            )}
          </div>
        )}

        {section === "Runs" && (
          <div className="border border-black/10" data-testid="brain-runs-section">
            <div className="p-5 border-b border-black/10 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="fx-mono-tag">LOCAL RUN HISTORY</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Verified runs</h2>
              </div>
              {canReadEvidence && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRunsReload((value) => value + 1)}
                  disabled={runs.state === "loading"}
                >
                  Refresh runs
                </Button>
              )}
            </div>
            {!canReadEvidence && (
              <div className="p-10 text-center" data-testid="brain-local-evidence-locked">
                <div className="fx-mono-tag">LOCAL ACCESS REQUIRED</div>
                <h3 className="mt-3 text-xl font-bold">Run history is unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Evidence can only be read by an Admin through the direct loopback Brain adapter.
                </p>
              </div>
            )}
            {canReadEvidence && runs.state === "loading" && (
              <div className="p-10 text-center text-sm text-neutral-500">Loading verified run history…</div>
            )}
            {canReadEvidence && runs.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                Run history blocked: {runs.error}
              </div>
            )}
            {canReadEvidence && runs.state === "ready" && (
              <div className="divide-y divide-black/10">
                {runs.data?.rejected_runs > 0 && (
                  <div className="bg-[#FFF7D6] px-5 py-3 text-sm" role="alert">
                    {runs.data.rejected_runs} rejected evidence item(s) were excluded.
                  </div>
                )}
                {runs.data?.runs?.length === 0 && (
                  <div className="p-10 text-center text-sm text-neutral-500">No persisted runs found.</div>
                )}
                {runs.data?.runs?.map((run) => (
                  <div
                    key={run.run_id}
                    className="p-5 grid lg:grid-cols-[1fr_auto] gap-4 items-center"
                    data-testid={`brain-run-${run.run_id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="fx-badge" style={{ color: run.status === "PASSED" ? "#0F766E" : "#B91C1C" }}>
                          {run.status}
                        </span>
                        <span className="font-mono text-xs break-all">{run.run_id}</span>
                      </div>
                      <div className="mt-2 text-sm text-neutral-500">
                        {run.started_at} · {run.verified_files} files verified
                      </div>
                    </div>
                    <Button type="button" variant="outline" onClick={() => openEvidence(run.run_id)}>
                      View evidence
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {section === "Approvals" && (
          <div className="border border-black/10" data-testid="brain-approvals-section">
            <div className="p-5 border-b border-black/10 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="fx-mono-tag">EXPLICIT OWNER CONTROL</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Evidence approvals</h2>
              </div>
              {canReadEvidence && (
                <Button type="button" variant="outline" onClick={() => setApprovalsReload((value) => value + 1)} disabled={approvals.state === "loading"}>
                  Refresh approvals
                </Button>
              )}
            </div>
            {!canReadEvidence && (
              <div className="p-10 text-center" data-testid="brain-local-approvals-locked">
                <div className="fx-mono-tag">LOCAL OWNER ACCESS REQUIRED</div>
                <h3 className="mt-3 text-xl font-bold">Approvals are unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Decisions require an Admin on the direct loopback adapter. Approval never triggers execution or deployment.
                </p>
              </div>
            )}
            {canReadEvidence && (runs.state === "loading" || approvals.state === "loading") && (
              <div className="p-10 text-center text-sm text-neutral-500">Loading verified approval records…</div>
            )}
            {canReadEvidence && runs.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                Approval evidence blocked: {runs.error}
              </div>
            )}
            {canReadEvidence && approvals.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                Approvals blocked: {approvals.error}
              </div>
            )}
            {canReadEvidence && approvalAction.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                Owner action blocked: {approvalAction.error}
              </div>
            )}
            {canReadEvidence && runs.state === "ready" && approvals.state === "ready" && (
              <div className="divide-y divide-black/10">
                {approvals.data?.rejected_approvals > 0 && (
                  <div className="bg-[#FFF7D6] px-5 py-3 text-sm" role="alert">
                    {approvals.data.rejected_approvals} rejected approval record(s) were excluded.
                  </div>
                )}
                {approvals.data?.approvals?.map((approval) => (
                  <div key={approval.approval_id} className="p-5 space-y-4" data-testid={`brain-approval-${approval.approval_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="fx-badge" style={{ color: approval.state === "APPROVED" ? "#0F766E" : approval.state === "REJECTED" ? "#B91C1C" : "#9A6700" }}>
                            {approval.state}
                          </span>
                          <span className="font-mono text-xs break-all">{approval.run_id}</span>
                        </div>
                        <div className="mt-2 text-sm text-neutral-500">
                          Requested by {approval.requested_by} · {approval.requested_at}
                        </div>
                      </div>
                      <Button type="button" variant="outline" onClick={() => openEvidence(approval.run_id)}>
                        Review evidence
                      </Button>
                    </div>
                    {approval.state === "PENDING_OWNER" && (
                      <div className="border border-black/10 bg-[#F8FAFC] p-4">
                        <Textarea value={approvalNote} onChange={(event) => setApprovalNote(event.target.value)} maxLength={1000} aria-label="Owner decision note" placeholder="Optional owner decision note" className="min-h-20 bg-white" />
                        <div className="mt-3 flex flex-wrap justify-end gap-2">
                          <Button type="button" variant="outline" disabled={approvalAction.state === "running"} onClick={() => decideApproval(approval.approval_id, "REJECTED")}>
                            Reject evidence
                          </Button>
                          <Button type="button" disabled={approvalAction.state === "running"} onClick={() => decideApproval(approval.approval_id, "APPROVED")} className="fx-btn-primary">
                            Approve evidence
                          </Button>
                        </div>
                      </div>
                    )}
                    {approval.state !== "PENDING_OWNER" && (
                      <div className="bg-[#F8FAFC] p-4 text-sm">
                        Final owner decision by {approval.decided_by} · {approval.note || "No decision note."}
                      </div>
                    )}
                  </div>
                ))}
                {runs.data?.runs?.filter((run) => (
                  run.status === "PASSED" && !approvals.data?.approvals?.some((approval) => approval.run_id === run.run_id)
                )).map((run) => (
                  <div key={run.run_id} className="p-5 grid lg:grid-cols-[1fr_auto] gap-4 items-center">
                    <div className="min-w-0">
                      <div className="fx-mono-tag">VERIFIED EVIDENCE READY</div>
                      <div className="mt-2 font-mono text-xs break-all">{run.run_id}</div>
                      <div className="mt-2 text-sm text-neutral-500">{run.verified_files} files verified</div>
                    </div>
                    <Button type="button" variant="outline" disabled={approvalAction.state === "running"} onClick={() => requestApproval(run.run_id)}>
                      Request owner approval
                    </Button>
                  </div>
                ))}
                {approvals.data?.approvals?.length === 0 && runs.data?.runs?.filter((run) => run.status === "PASSED").length === 0 && (
                  <div className="p-10 text-center text-sm text-neutral-500">No verified passing evidence is ready for approval.</div>
                )}
                <div className="bg-[#F8FAFC] p-4 text-sm">
                  Approval record only · No execution · Production blocked · Deployment blocked · Git push blocked · Self-approval blocked
                </div>
              </div>
            )}
          </div>
        )}

        {section === "Tools" && (
          <div className="border border-black/10" data-testid="brain-tools-section">
            <div className="p-5 border-b border-black/10 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="fx-mono-tag">RESTRICTED LOCAL BUILDER</div>
                <h2 className="mt-2 text-2xl font-bold tracking-tight">Tool candidates</h2>
                <p className="mt-2 text-sm text-neutral-500">
                  Build and test a candidate from an owner work order. Candidates are never installed automatically.
                </p>
              </div>
              {canUseTools && (
                <Button type="button" variant="outline" onClick={() => setToolsReload((value) => value + 1)}>Refresh tools</Button>
              )}
            </div>
            {!canUseTools && (
              <div className="p-10 text-center" data-testid="brain-local-tools-locked">
                <div className="fx-mono-tag">BUILDER GATES CLOSED</div>
                <h3 className="mt-3 text-xl font-bold">Tool Builder is unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Direct loopback, Projects, execution and the separate Builder gate are all required.
                </p>
              </div>
            )}
            {canUseTools && (tools.state === "loading" || projects.state === "loading") && (
              <div className="p-10 text-center text-sm text-neutral-500">Loading local Builder workspace…</div>
            )}
            {canUseTools && tools.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">Tools blocked: {tools.error}</div>
            )}
            {canUseTools && projects.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">Builder projects blocked: {projects.error}</div>
            )}
            {canUseTools && toolAction.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">Candidate build blocked: {toolAction.error}</div>
            )}
            {canUseTools && tools.state === "ready" && projects.state === "ready" && (
              <div className="divide-y divide-black/10">
                <div className="p-5">
                  <div className="fx-mono-tag mb-3">READY WORK ORDERS</div>
                  <div className="space-y-3">
                    {projects.data?.projects?.filter((project) => project.state === "READY_FOR_BUILD").map((project) => (
                      <div key={project.project_id} className="border border-black/10 p-4 grid lg:grid-cols-[1fr_auto] gap-4 items-center">
                        <div className="min-w-0">
                          <strong>{project.title}</strong>
                          <div className="mt-1 font-mono text-xs break-all">{project.project_id}</div>
                          <div className="mt-2 text-sm text-neutral-500">{project.objective}</div>
                        </div>
                        <Button type="button" disabled={toolAction.state === "running"} onClick={() => buildTool(project.project_id)} className="fx-btn-primary">
                          {toolAction.state === "running" && toolAction.target === project.project_id ? "Building candidate…" : "Build candidate"}
                        </Button>
                      </div>
                    ))}
                    {projects.data?.projects?.filter((project) => project.state === "READY_FOR_BUILD").length === 0 && (
                      <div className="border border-black/10 p-5 text-sm text-neutral-500">No READY_FOR_BUILD work order is available.</div>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  <div className="fx-mono-tag mb-3">VERIFIED CANDIDATES</div>
                  <div className="space-y-3">
                    {tools.data?.tools?.map((tool) => (
                      <div key={tool.run_id} className="border border-black/10 p-4" data-testid={`brain-tool-${tool.run_id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="fx-badge" style={{ color: tool.status === "PASSED" ? "#0F766E" : "#B91C1C" }}>{tool.status}</span>
                              <strong>{tool.tool?.name}</strong>
                            </div>
                            <div className="mt-2 font-mono text-xs break-all">{tool.run_id}</div>
                            <div className="mt-2 text-sm text-neutral-500">{tool.tool?.description}</div>
                            <div className="mt-2 text-xs text-neutral-500">{tool.tool?.test_count} restricted test(s) · {tool.tool?.model}</div>
                          </div>
                          <Button type="button" variant="outline" onClick={() => openEvidence(tool.run_id)}>Review evidence</Button>
                        </div>
                      </div>
                    ))}
                    {tools.data?.tools?.length === 0 && (
                      <div className="border border-black/10 p-5 text-sm text-neutral-500">No tool candidate has been built.</div>
                    )}
                  </div>
                </div>
                <div className="bg-[#F8FAFC] p-4 text-sm">
                  Candidate only · Restricted tests · No installation · Production blocked · Deployment blocked · Git push blocked · Owner approval required
                </div>
              </div>
            )}
          </div>
        )}

        {section === "Evidence" && (
          <div className="border border-black/10" data-testid="brain-evidence-section">
            <div className="p-5 border-b border-black/10">
              <div className="fx-mono-tag">READ-ONLY EVIDENCE</div>
              <h2 className="mt-2 text-2xl font-bold tracking-tight">Run evidence</h2>
            </div>
            {!canReadEvidence && (
              <div className="p-10 text-center" data-testid="brain-local-evidence-locked">
                <div className="fx-mono-tag">LOCAL ACCESS REQUIRED</div>
                <h3 className="mt-3 text-xl font-bold">Evidence is unavailable here</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  Production, deployment, Git push and self-approval remain blocked.
                </p>
              </div>
            )}
            {canReadEvidence && evidence.state === "idle" && runs.state === "loading" && (
              <div className="p-10 text-center text-sm text-neutral-500">Loading verified evidence index…</div>
            )}
            {canReadEvidence && evidence.state === "idle" && runs.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                Evidence index blocked: {runs.error}
              </div>
            )}
            {canReadEvidence && evidence.state === "idle" && runs.state === "ready" && (
              <div className="divide-y divide-black/10">
                {runs.data?.runs?.length === 0 && (
                  <div className="p-10 text-center text-sm text-neutral-500">No evidence is available.</div>
                )}
                {runs.data?.runs?.map((run) => (
                  <button
                    key={run.run_id}
                    type="button"
                    onClick={() => openEvidence(run.run_id)}
                    className="w-full p-5 text-left hover:bg-[#F8FAFC] flex flex-wrap items-center justify-between gap-3"
                  >
                    <span className="font-mono text-xs break-all">{run.run_id}</span>
                    <span className="text-sm font-semibold">Open verified result →</span>
                  </button>
                ))}
              </div>
            )}
            {canReadEvidence && evidence.state === "loading" && (
              <div className="p-10 text-center text-sm text-neutral-500">Validating result.json…</div>
            )}
            {canReadEvidence && evidence.state === "error" && (
              <div className="m-5 border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
                Evidence blocked: {evidence.error}
              </div>
            )}
            {canReadEvidence && evidence.state === "ready" && (
              <div className="p-5 space-y-5" data-testid="brain-evidence-detail">
                <div className="grid md:grid-cols-3 gap-4">
                  <div><div className="text-xs text-neutral-500">Status</div><strong>{evidence.data.status}</strong></div>
                  <div><div className="text-xs text-neutral-500">Verified files</div><strong>{evidence.data.source_manifest?.verified_files}</strong></div>
                  <div><div className="text-xs text-neutral-500">Evidence state</div><strong>{evidence.data.evidence?.state}</strong></div>
                </div>
                <div className="border border-black/10 p-4">
                  <div className="fx-mono-tag mb-2">RUN ID</div>
                  <div className="font-mono text-xs break-all">{evidence.data.run_id}</div>
                </div>
                {evidence.data.kind === "BRAIN_TOOL_BUILD" && (
                  <div className="border border-black/10 p-4 grid md:grid-cols-3 gap-4">
                    <div><div className="text-xs text-neutral-500">Candidate</div><strong>{evidence.data.tool?.name}</strong></div>
                    <div><div className="text-xs text-neutral-500">Restricted tests</div><strong>{evidence.data.tool?.test_count}</strong></div>
                    <div><div className="text-xs text-neutral-500">Local model</div><strong>{evidence.data.tool?.model}</strong></div>
                    <div className="md:col-span-3"><div className="text-xs text-neutral-500">Project</div><div className="font-mono text-xs break-all">{evidence.data.project_id}</div></div>
                  </div>
                )}
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="border border-black/10 p-4 min-w-0">
                    <div className="fx-mono-tag mb-2">OUTPUT SHA-256</div>
                    <div className="font-mono text-xs break-all">{evidence.data.output_sha256}</div>
                  </div>
                  <div className="border border-black/10 p-4 min-w-0">
                    <div className="fx-mono-tag mb-2">RESULT SHA-256</div>
                    <div className="font-mono text-xs break-all">{evidence.data.evidence?.result_sha256}</div>
                  </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="border border-black/10 p-4 min-w-0">
                    <div className="fx-mono-tag mb-2">STDOUT</div>
                    <pre className="text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto">{evidence.data.stdout || "No stdout captured."}</pre>
                  </div>
                  <div className="border border-black/10 p-4 min-w-0">
                    <div className="fx-mono-tag mb-2">STDERR</div>
                    <pre className="text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto">{evidence.data.stderr || "No stderr captured."}</pre>
                  </div>
                </div>
                <div className="bg-[#F8FAFC] p-4 text-sm">
                  Local only · Read only · Production blocked · Deployment blocked · Self-approval blocked
                </div>
              </div>
            )}
          </div>
        )}

        {!["Overview", "Chat", "Projects", "Runs", "Approvals", "Tools", "Evidence"].includes(section) && (
          <div className="border border-black/10 p-10 text-center" data-testid={`brain-section-${section.toLowerCase()}`}>
            <div className="fx-mono-tag">{section.toUpperCase()}</div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight">{section} workspace</h2>
            <p className="mt-2 text-sm text-neutral-500">
              This controlled area will be connected to the local Brain adapter in the next slice.
            </p>
          </div>
        )}
      </div>
    </AppShell>
  );
}
